import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PWAInstallService {
  private deferredPrompt: any;

  // Observable: true = show install banner, false = hide
  private bannerVisibleSubject = new BehaviorSubject<boolean>(false);
  public readonly bannerVisible$ = this.bannerVisibleSubject.asObservable();
  private readonly BANNER_TRACK_KEY = 'pwaBannerLastShown';

  constructor() {
    // Listen for beforeinstallprompt (Android Chrome + desktop Chrome/Edge)
    window.addEventListener('beforeinstallprompt', (e: any) => {
      e.preventDefault();
      this.deferredPrompt = e;

      if (
        this.isAndroid() &&
        environment.settings?.installPWA &&
        this.shouldShowBanner()
      ) {
        this.updateBannerStatus(true);
      }
    });

    // When the app is installed, hide banner
    window.addEventListener('appinstalled', () => {
      this.updateBannerStatus(false);
      this.deferredPrompt = null;
      localStorage.removeItem(this.BANNER_TRACK_KEY);
    });

    // Check on load if app is already installed
    this.checkInstallState();
  }

  private checkInstallState() {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone;

    if (isStandalone) {
      this.updateBannerStatus(false);
    }
  }

  private isAndroid(): boolean {
    return /android/i.test(navigator.userAgent);
  }

  public async install(): Promise<void> {
    if (!this.deferredPrompt) {
      this.updateBannerStatus(false);
      return;
    }

    this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      return;
    } else {
      this.updateBannerStatus(false);
    }

    this.deferredPrompt = null;
  }

  /** Called when user closes banner */
  public dismiss() {
    this.updateBannerStatus(false);
  }

  private updateBannerStatus(status: boolean) {
    this.bannerVisibleSubject.next(status);
  }

  private shouldShowBanner(): boolean {
    const now = new Date();
    const currentDay = now.toDateString(); // identifies the day uniquely
    const currentWeek = this.getWeekNumber(now);

    const tracking = this.getTrackingData();

    // Reset tracking if we're in a new week
    if (tracking.week !== currentWeek) {
      tracking.week = currentWeek;
      tracking.daysShown = [];
    }

    // Check if already shown today
    if (tracking.daysShown.includes(currentDay)) {
      return false; // already shown once today
    }

    // Check if shown twice already this week
    if (tracking.daysShown.length >= 2) {
      return false; // already shown twice this week
    }

    // Otherwise, show banner & update tracking
    tracking.daysShown.push(currentDay);
    this.saveTrackingData(tracking);
    return true;
  }

  private getTrackingData(): { week: number; daysShown: string[] } {
    const data = localStorage.getItem(this.BANNER_TRACK_KEY);
    if (data) {
      return JSON.parse(data);
    }
    return { week: this.getWeekNumber(new Date()), daysShown: [] };
  }

  private saveTrackingData(data: { week: number; daysShown: string[] }) {
    localStorage.setItem(this.BANNER_TRACK_KEY, JSON.stringify(data));
  }

  private getWeekNumber(date: Date): number {
    // ISO week number calculation
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear =
      (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }
}
